import {
  COMPOSITION_GET_DATA_V3,
  type CompositionV3DataQuery,
  type CompositionV3Req,
  EntKind,
  GET_SCREENER_DATA,
  POSTINGS_V3_GET_DATA,
  type PostingsV3DataQuery,
  type PostingsV3Ent,
  QUERY_SENTIMENT_V3,
  type QuerySentimentV3Query,
  type QuerySentimentV3QueryVariables,
  ScreenerDimension,
  type ScreenerReq,
  type ScreenerRespEntity,
  ScreenerSortCol,
  type SentimentV3Ent,
} from '@revelio/data-access';

import { useEffectiveFilters } from '../../../filters/hooks/useEffectiveFilters';
import {
  FilterId,
  FilterType,
  type PrimaryFilterId,
} from '../../../filters/types/filters';
import { getCompositionV3Variables } from '../../../filters/utils/composition-v3/getCompositionV3Variables';
import { buildScreenerFilters } from '../../../filters/utils/get-screener-filters';
import { buildSentimentV3VariablesFromEntities } from '../../../filters/utils/get-sentiment-v3-variables';
import { buildPostingsV3VariablesFromEntities } from '../../../filters/utils/getPostingsV3Variables';
import { useChart } from '../../Chart/ChartProvider';
import { useCompositionV3QueryWithStatus } from '../../DataResolver/use-composition-v3-query';
import { useTabContentConfig } from '../../PageConfigProvider/PageConfigProvider';
import { PlotFilterType } from '../../PageConfigProvider/types/item';
import {
  isPostingMetric,
  isScreenerMetric,
  isSentimentMetric,
  type MetricValues,
  type PostingMetricValues,
  type ScreenerMetricValues,
  type SentimentMetricValues,
} from '../../PageConfigProvider/types/metric';
import type { MultiMetricProviderResult } from '../MultiMetricProvider';
import { type DataProviderChildren, DataType } from '../types';
import {
  combineQueryStatus,
  useUrqlQueryWithStatus,
} from '../utils/data-provider-results-utils';
import { processMultipleScreenerMetrics } from '../utils/screener-metrics-utils';
import { useSnowflakeSavedQuery } from '../utils/useSnowflakeSavedQuery';
import {
  buildPostingsByRcidFromPostingsV3,
  extractActivePostingsFromPostingsV3,
} from './convert-postings-v3-to-market-metrics';

const DEFAULT_DISCOVERY_LIMIT = 10;

const getLatestFromSeries = (
  series: ReadonlyArray<{ value?: number | null } | null> | null | undefined
): number | null => {
  if (!Array.isArray(series) || series.length === 0) return null;
  const last = series[series.length - 1];
  return typeof last?.value === 'number' ? last.value : null;
};

const extractSentimentMetricValues = (
  sentimentEntities: NonNullable<
    QuerySentimentV3Query['sentiment_v3']
  >['entities'],
  screenerEntities: ScreenerRespEntity[],
  metric: SentimentMetricValues
): Array<{ dim: string; value: number }> => {
  if (metric === 'sentiment_top_bottom') return [];

  return screenerEntities.flatMap((entity) => {
    const label = entity.long_name || entity.short_name || '';
    const sentimentEntity = sentimentEntities.find(
      (sentimentEntity) => sentimentEntity.id === entity.entity_id
    );

    const value = getLatestFromSeries(sentimentEntity?.metrics[metric]);
    if (value === null) return [];

    return [{ dim: label, value: value / 100 }];
  });
};

const extractMarketShare = (
  screenerEntities: ScreenerRespEntity[],
  headcountByCompany: Map<string, number>,
  totalHeadcount: number | null
): Array<{ dim: string; value: number }> =>
  screenerEntities.flatMap((entity) => {
    const label = entity.long_name || entity.short_name || '';
    const companyHeadcount = headcountByCompany.get(label) ?? null;
    if (
      companyHeadcount === null ||
      totalHeadcount === null ||
      totalHeadcount <= 0
    ) {
      return [];
    }
    return [{ dim: label, value: companyHeadcount / totalHeadcount }];
  });

type SnowflakeRow = Record<string, unknown>;

const quantile = (values: number[], q: number): number => {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const pos = (sorted.length - 1) * q;
  const base = Math.floor(pos);
  const rest = pos - base;
  const baseVal = sorted[base] ?? 0;
  const nextVal = sorted[base + 1];
  return nextVal !== undefined ? baseVal + rest * (nextVal - baseVal) : baseVal;
};

const findTargetCompanies = (rows: SnowflakeRow[]): string[] => {
  const companies = rows.map((row) => ({
    name: String(row['Company'] ?? ''),
    salary:
      typeof row['Salary'] === 'number' ? (row['Salary'] as number) : null,
    sentiment:
      typeof row['Sentiment Rating'] === 'number'
        ? (row['Sentiment Rating'] as number)
        : null,
    prestige: String(row['Prestige'] ?? ''),
  }));

  const salaries = companies
    .map((c) => c.salary)
    .filter((v): v is number => v !== null);
  const sentiments = companies
    .map((c) => c.sentiment)
    .filter((v): v is number => v !== null);
  const highPrestigeNames = new Set(
    companies
      .filter((c) => c.prestige === 'High' || c.prestige === 'Very High')
      .map((c) => c.name)
  );

  const findAtThreshold = (salaryQ: number, sentimentQ: number) => {
    const salaryThreshold =
      salaries.length > 0 ? quantile(salaries, salaryQ) : 0;
    const sentimentThreshold =
      sentiments.length > 0 ? quantile(sentiments, sentimentQ) : 0;
    return companies
      .filter(
        (c) =>
          c.salary !== null &&
          c.salary < salaryThreshold &&
          c.sentiment !== null &&
          c.sentiment < sentimentThreshold &&
          highPrestigeNames.has(c.name)
      )
      .map((c) => c.name);
  };

  const targets = findAtThreshold(0.33, 0.33);
  return targets.length > 0 ? targets : findAtThreshold(0.5, 0.5);
};

const buildSnowflakeTableRows = (
  snowflakeRows: SnowflakeRow[],
  postingsByRcid: Map<number, number>,
  headcountColumnName: string
) => {
  const rows = snowflakeRows.map((row) => {
    const rcid = row['rcid'];
    return {
      Company: String(row['Company'] ?? ''),
      [headcountColumnName]: row['Headcount'],
      'Market Share':
        typeof row['Market Share'] === 'number'
          ? (row['Market Share'] as number) / 100
          : null,
      Salary: row['Salary'],
      'Active Postings':
        typeof rcid === 'number' ? (postingsByRcid.get(rcid) ?? null) : null,
      'Sentiment Rating': row['Sentiment Rating'],
      prestige: row['Prestige'] ?? '',
    };
  });

  const columnMetrics: Record<string, string> = {
    [headcountColumnName]: 'screener_headcount',
    'Market Share': 'market_share',
    Salary: 'screener_salary',
    'Active Postings': 'postings_active',
    'Sentiment Rating': 'demand_supply',
  };

  const columnFormats: Record<string, string> = {
    'Market Share': '.2%',
  };

  return { rows, columnMetrics, columnFormats };
};

const extractSnowflakeMarketShare = (
  snowflakeRows: SnowflakeRow[]
): Array<{ dim: string; value: number }> =>
  snowflakeRows.flatMap((row) => {
    const dim = String(row['Company'] ?? '');
    const value = row['Market Share'];
    return typeof value === 'number' ? [{ dim, value: value / 100 }] : [];
  });

const getTotalHeadcount = (
  compositionData: CompositionV3DataQuery | undefined
): number | null => {
  const headcountTs =
    compositionData?.composition_v3?.entities?.[0]?.metrics?.headcount;
  if (!Array.isArray(headcountTs) || headcountTs.length === 0) return null;
  const last = headcountTs[headcountTs.length - 1];
  return typeof last?.val === 'number' ? last.val : null;
};

/**
 * Fetches screener data to discover top entities, then optionally chains
 * sentiment, postings, and composition queries for those entities. Returns
 * each requested metric as its own DataType.SINGLE entry in the byMetric
 * map so the standard table pipeline can merge columns.
 */
type CompetitiveLandscapeProviderProps = {
  primaryDimension: PrimaryFilterId;
  metrics: MetricValues[];
  children: DataProviderChildren<MultiMetricProviderResult>;
};

export const CompetitiveLandscapeProvider = ({
  primaryDimension,
  metrics,
  children,
}: CompetitiveLandscapeProviderProps) => {
  const { item } = useChart();
  const { items: tabItems } = useTabContentConfig();

  // Resolve the Snowflake config for competitive landscape data by finding
  // the table item that shares the same dimensionSource. This lets text
  // charts reuse the table's config without duplicating it.
  const snowflakeConfig = tabItems.find(
    (sibling) =>
      sibling.plot?.dimensionSource === item?.plot?.dimensionSource &&
      sibling.plot?.chartType === 'table' &&
      sibling.plot?.snowflakeConfig
  )?.plot?.snowflakeConfig;
  const effectiveFilters = useEffectiveFilters();
  const compositionEffectiveFilters = useEffectiveFilters({
    viewGranularity: 'month',
  });

  const discoveryLimit =
    item?.plot?.plotFilters?.find((f) => f.type === PlotFilterType.TOP_N)
      ?.value ?? DEFAULT_DISCOVERY_LIMIT;

  const roleFilter = effectiveFilters.find(
    (f) => f.id === FilterId.ROLE && f.type === FilterType.TREE
  );
  const headcountColumnName =
    roleFilter?.type === FilterType.TREE && roleFilter.selections?.[0]?.name
      ? `${roleFilter.selections[0].name} Roles`
      : 'Headcount';

  const screenerMetrics = metrics.filter((m): m is ScreenerMetricValues =>
    isScreenerMetric(m)
  );
  const sentimentMetrics = metrics.filter((m): m is SentimentMetricValues =>
    isSentimentMetric(m)
  );
  const postingMetrics = metrics.filter((m): m is PostingMetricValues =>
    isPostingMetric(m)
  );
  const needsMarketShare = metrics.includes('market_share');

  // Screener query discovers the top entities to plot.
  const screenerFilters: ScreenerReq = (() => {
    const firstMetric = screenerMetrics[0] ?? 'screener_headcount';
    const base = buildScreenerFilters({
      effectiveFilters,
      primaryDimension,
      metric: firstMetric,
      pagination: { limit: discoveryLimit, offset: 0 },
      sorting: {
        sort_by: ScreenerSortCol.SortByHeadcount,
        ascending: false,
        num_col: 1,
      },
    });

    const headcountSegments = base.segments?.headcount;
    const updatedBase =
      Array.isArray(headcountSegments) && headcountSegments.length > 0
        ? {
            ...base,
            segments: {
              ...base.segments,
              salary: headcountSegments.map((seg) => ({
                ...seg,
                start_val: 0,
              })),
            },
          }
        : base;

    // The provider rejects `company_info_selectors` on any non-company dim.
    if (updatedBase.dim !== ScreenerDimension.Company) return updatedBase;

    return {
      ...updatedBase,
      entity_info_selectors: {
        ...updatedBase.entity_info_selectors,
        company_info_selectors: {
          ...updatedBase.entity_info_selectors?.company_info_selectors,
          is_subsidiary: false,
        },
      },
    };
  })();

  const [{ data: screenerData, status: screenerStatus }] =
    useUrqlQueryWithStatus<
      { screener: { entities: ScreenerRespEntity[] } },
      { filters: ScreenerReq }
    >({
      query: GET_SCREENER_DATA,
      variables: { filters: screenerFilters },
      pause: !screenerFilters.dim,
    });

  const entities = screenerData?.screener?.entities ?? [];
  const companyIds = entities.map((e) => e.entity_id);
  const hasEntities = companyIds.length > 0;

  const shouldFetchSentiment = sentimentMetrics.length > 0 && hasEntities;
  const sentimentV3Entities: SentimentV3Ent[] = companyIds.map((EntID) => ({
    EntID,
    EntType: EntKind.Company,
  }));
  const sentimentV3Variables = buildSentimentV3VariablesFromEntities({
    entities: sentimentV3Entities,
    filters: effectiveFilters,
    primaryDimension: FilterId.COMPANY,
    isSnapshot: true,
  });

  const [{ data: sentimentData, status: sentimentStatus }] =
    useUrqlQueryWithStatus<
      QuerySentimentV3Query,
      QuerySentimentV3QueryVariables
    >({
      query: QUERY_SENTIMENT_V3,
      variables: sentimentV3Variables,
      pause: !shouldFetchSentiment,
    });

  // Snowflake query supplies sentiment rating + prestige + core columns.
  const {
    data: snowflakeRows,
    status: snowflakeStatus,
    enabled: snowflakeEnabled,
  } = useSnowflakeSavedQuery(snowflakeConfig);

  // When Snowflake data is available, use its rcids for the postings lookup.
  const snowflakeRcids = (snowflakeRows ?? [])
    .map((r) => r['rcid'])
    .filter((id): id is number => typeof id === 'number');
  const postingsCompanyIds =
    snowflakeConfig && snowflakeRcids.length > 0 ? snowflakeRcids : companyIds;
  const hasPostingsCompanies = postingsCompanyIds.length > 0;
  const shouldFetchPostings = postingMetrics.length > 0 && hasPostingsCompanies;

  // The company ids to plot come from the screener/Snowflake, not the primary
  // filter, so build from an explicit entity list. Each response entity is keyed
  // by `entity.index`, zipped back to these request entities to recover the
  // company id. NORMAL mode (default) returns counts on `metrics.active`.
  const postingsV3Entities: PostingsV3Ent[] = postingsCompanyIds.map(
    (EntID) => ({ EntID, EntType: EntKind.Company })
  );
  const postingsV3Variables = buildPostingsV3VariablesFromEntities({
    entities: postingsV3Entities,
    effectiveFilters,
  });

  const [{ data: postingsDataV3, status: postingsStatusV3 }] =
    useUrqlQueryWithStatus<PostingsV3DataQuery>({
      query: POSTINGS_V3_GET_DATA,
      variables: postingsV3Variables,
      pause: !shouldFetchPostings,
    });

  // Composition query supplies the market-share denominator, so neither entity
  // selection may scope it — that would cancel out of the share.
  const filtersForComposition = compositionEffectiveFilters.filter(
    (f) => f.id !== FilterId.COMPANY && f.id !== FilterId.INDUSTRY
  );
  const compositionReqV3: CompositionV3Req = getCompositionV3Variables({
    primaryDimension: FilterId.ROLE,
    effectiveFilters: filtersForComposition,
    isSnapshot: true,
  });

  const compositionPausedV3 =
    !needsMarketShare ||
    !hasEntities ||
    (Array.isArray(compositionReqV3.entities) &&
      compositionReqV3.entities.length === 0) ||
    !compositionReqV3.filters?.start_date ||
    !compositionReqV3.filters?.end_date;

  const [{ data: compositionDataUnion, status: compositionStatusUnion }] =
    useCompositionV3QueryWithStatus({
      query: COMPOSITION_GET_DATA_V3,
      variables: { req: compositionReqV3 },
      pause: compositionPausedV3,
    });

  const compositionDataForHeadcount = compositionDataUnion;

  const compositionStatusForCombine = compositionStatusUnion;

  const compositionFetchPaused = compositionPausedV3;

  const processedScreener = processMultipleScreenerMetrics(
    entities,
    screenerMetrics
  );
  const headcountByCompany = new Map(
    processMultipleScreenerMetrics(entities, ['screener_headcount'])[
      'screener_headcount'
    ]?.map((p) => [p.dim, p.value]) ?? []
  );
  const entityLabelByRcid = new Map(
    entities.map((entity) => [
      entity.entity_id,
      entity.long_name || entity.short_name || '',
    ])
  );
  const totalHeadcount = getTotalHeadcount(compositionDataForHeadcount);

  const sentimentResults = sentimentData?.sentiment_v3?.entities ?? [];

  const getMetricData = (
    m: MetricValues
  ): Array<{ dim: string; value: number }> => {
    if (isScreenerMetric(m)) {
      return processedScreener[m] ?? [];
    }
    if (isSentimentMetric(m)) {
      return extractSentimentMetricValues(sentimentResults, entities, m);
    }
    if (m === 'postings_active') {
      return extractActivePostingsFromPostingsV3({
        data: postingsDataV3,
        requestEntities: postingsV3Entities,
        entityLabelByRcid,
      });
    }
    if (m === 'market_share') {
      return extractMarketShare(entities, headcountByCompany, totalHeadcount);
    }
    return [];
  };

  const byMetric: MultiMetricProviderResult['byMetric'] = {};

  if (snowflakeConfig && snowflakeRows && snowflakeRows.length > 0) {
    const postingsByRcid = buildPostingsByRcidFromPostingsV3({
      data: postingsDataV3,
      requestEntities: postingsV3Entities,
    });
    const { rows, columnMetrics, columnFormats } = buildSnowflakeTableRows(
      snowflakeRows,
      postingsByRcid,
      headcountColumnName
    );

    const targetCompanies = findTargetCompanies(snowflakeRows);
    if (targetCompanies.length > 0) {
      byMetric['target_companies_rec'] = {
        type: DataType.SINGLE,
        data: targetCompanies.map((name, i) => ({
          dim: name,
          value: targetCompanies.length - i,
        })),
      };
    }

    byMetric['market_share'] = {
      type: DataType.SINGLE,
      data: extractSnowflakeMarketShare(snowflakeRows),
    };

    byMetric['screener_headcount'] = {
      type: DataType.TABLE_ROWS,
      data: rows.map((row) => ({
        ...row,
        __columnMetrics: columnMetrics,
        __columnFormats: columnFormats,
      })),
    };
  } else {
    const headcountData = getMetricData('screener_headcount');
    const metricDataByCompany = new Map<string, Record<string, unknown>>();

    for (const item of headcountData) {
      metricDataByCompany.set(item.dim, {
        Company: item.dim,
        [headcountColumnName]: item.value,
      });
    }

    const columnMetrics: Record<string, string> = {
      [headcountColumnName]: 'screener_headcount',
    };

    for (const m of metrics) {
      if (m === 'screener_headcount') continue;
      if (m === 'market_share' && snowflakeConfig) continue;
      const metricData = getMetricData(m);
      const metricLabelMap: Record<string, string> = {
        market_share: 'Market Share',
        screener_salary: 'Salary',
        postings_active: 'Active Postings',
        overall: 'Sentiment Rating',
      };
      const label = metricLabelMap[m] ?? m;
      columnMetrics[label] = m;

      for (const d of metricData) {
        const row = metricDataByCompany.get(d.dim);
        if (row) {
          row[label] = d.value;
        }
      }
    }

    const columnFormats: Record<string, string> = {
      'Market Share': '.2%',
    };

    const rows = Array.from(metricDataByCompany.values());

    byMetric['screener_headcount'] = {
      type: DataType.TABLE_ROWS,
      data: rows.map((row) => ({
        ...row,
        __columnMetrics: columnMetrics,
        __columnFormats: columnFormats,
      })),
    };
  }

  const statuses = [screenerStatus];
  if (shouldFetchSentiment) statuses.push(sentimentStatus);
  if (shouldFetchPostings) statuses.push(postingsStatusV3);
  if (!compositionFetchPaused) statuses.push(compositionStatusForCombine);
  if (snowflakeEnabled) statuses.push(snowflakeStatus);
  const status = combineQueryStatus(statuses);

  return children({
    data: { byMetric },
    status,
  });
};
